import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  clearCachedCurrentUserProfile,
  getCachedCurrentUserProfile,
  setCachedCurrentUserProfile,
  type CurrentUserProfileCache,
} from "@/lib/profileCache";
import { setCachedUserName, setCachedUserInitials, computeInitials } from "@/lib/navCache";

const cacheNavName = (name: string | null | undefined) => {
  if (!name) return;
  setCachedUserName(name);
  setCachedUserInitials(computeInitials(name));
};

interface CurrentUserProfileContextValue {
  profile: CurrentUserProfileCache | null;
  isLoading: boolean;
}

const CurrentUserProfileContext = createContext<CurrentUserProfileContextValue>({
  profile: null,
  isLoading: true,
});

/**
 * Owns the single fetch + realtime subscription for the signed-in user's
 * profile, and shares the result with every consumer.
 *
 * This is a provider rather than a plain hook for a load-bearing reason. The
 * realtime channel topic is derived from the user id, so every mount of the old
 * hook asked for the *same* topic — and `supabase.channel(topic)` returns the
 * already-registered channel when one exists rather than creating a second one.
 * `FeedCard` calls this once per post, so on a feed with N posts, mounts 2..N
 * each called `.on("postgres_changes", …)` against a channel that was already
 * subscribed.
 *
 * Under @supabase/supabase-js 2.99 that was silently accepted. 2.112 added a
 * guard to `RealtimeChannel.on()` that throws when the channel is joined or
 * joining, so the same code became an uncaught error per extra mount and took
 * the whole feed down through the error boundary.
 *
 * One subscription for the whole app removes the duplicate-registration path
 * entirely, and is what the code was already assuming by using a stable topic.
 */
export const CurrentUserProfileProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [profile, setProfile] = useState<CurrentUserProfileCache | null>(() =>
    getCachedCurrentUserProfile()
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setProfile(null);
      setIsLoading(false);
      clearCachedCurrentUserProfile();
      return;
    }

    const cachedProfile = getCachedCurrentUserProfile(user.id);
    setProfile(cachedProfile);
    setIsLoading(!cachedProfile);

    let isActive = true;

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("full_name, avatar_url")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!isActive) return;
        if (error) throw error;

        if (data) {
          const nextProfile = {
            userId: user.id,
            full_name: data.full_name,
            avatar_url: data.avatar_url,
          } satisfies CurrentUserProfileCache;

          setProfile(nextProfile);
          setCachedCurrentUserProfile(nextProfile);
          cacheNavName(nextProfile.full_name);
        } else {
          setProfile(null);
          clearCachedCurrentUserProfile();
        }
      } catch (error) {
        console.error("Error fetching current user profile:", error);
        if (isActive && !cachedProfile) {
          setProfile(null);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    const channel = supabase
      .channel(`current-user-profile-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles", filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (!isActive) return;

          if (payload.eventType === "DELETE") {
            setProfile(null);
            clearCachedCurrentUserProfile();
            return;
          }

          const nextProfile = {
            userId: user.id,
            full_name: (payload.new as { full_name?: string | null })?.full_name ?? null,
            avatar_url: (payload.new as { avatar_url?: string | null })?.avatar_url ?? null,
          } satisfies CurrentUserProfileCache;

          setProfile(nextProfile);
          setCachedCurrentUserProfile(nextProfile);
          cacheNavName(nextProfile.full_name);
        }
      )
      .subscribe();

    fetchProfile();

    return () => {
      isActive = false;
      supabase.removeChannel(channel);
    };
  }, [authLoading, user]);

  return (
    <CurrentUserProfileContext.Provider
      value={{ profile, isLoading: authLoading || isLoading }}
    >
      {children}
    </CurrentUserProfileContext.Provider>
  );
};

/**
 * Read the signed-in user's profile. Shape is unchanged from the previous
 * hook-per-component implementation, so call sites did not need to change.
 */
export const useCurrentUserProfile = () => useContext(CurrentUserProfileContext);
